import { logger } from '@springtree/eva-sdk-core-logger';
import { getServiceSettings, setServiceSetting } from '@springtree/eva-sdk-core-service';
import { Core } from '@springtree/eva-services-core';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import getEvaEndpoint from '../helpers/get-eva-endpoint.js';
import applicationTokenState from '../state/core/application-token-state.js';
import evaEndpointUrlState from '../state/core/eva-endpoint-url-state.js';
import requestedOrganizationUnitIdState from '../state/core/requested-organization-unit-id-state.js';
import currentUserTokenState from '../state/current-user/current-user-token-state.js';
import shoppingCartState from '../state/checkout/shopping-cart-state.js';
import storage, { safeParseJson } from '../helpers/storage.js';
import useGetState from './use-get-state.js';
import orderDetailsState from '../state/order/order-details-state.js';
const storageKey = 'evaOrganizationUnitData';
export const useSwitchOrganizationUnit = () => {
    const currentApplicationToken = useGetState(applicationTokenState);
    const currentOrganizationUnitID = useGetState(requestedOrganizationUnitIdState);
    const currentUserToken = useGetState(currentUserTokenState);
    const evaEndpointUrl = useGetState(evaEndpointUrlState);
    const shoppingCartRequest = useGetState(shoppingCartState.request);
    const orderDetailsRequest = useGetState(orderDetailsState.request);
    const setApplicationToken = useSetRecoilState(applicationTokenState);
    const setCurrentUserToken = useSetRecoilState(currentUserTokenState);
    const setRequestedOrganizationUnitId = useSetRecoilState(requestedOrganizationUnitIdState);
    const setShoppingCartRequest = useSetRecoilState(shoppingCartState.request);
    const setOrderDetailsRequest = useSetRecoilState(orderDetailsState.request);
    const switchOrganizationUnit = useCallback(async (newOrganizationUnitID) => {
        if (!evaEndpointUrl) {
            return;
        }
        const evaEndpoint = await getEvaEndpoint(evaEndpointUrl);
        // Check if the organization unit differs from the current one. If not do nothing
        //
        if (newOrganizationUnitID === currentOrganizationUnitID) {
            return;
        }
        // Preserve all the current organization unit state in storage
        // And pull up any previous details
        //
        const organizationUnitData = (safeParseJson(storage.getItem(storageKey) || '') ?? {});
        if (currentOrganizationUnitID) {
            organizationUnitData[currentOrganizationUnitID] = {
                applicationToken: currentApplicationToken,
                authenticationToken: currentUserToken,
                shoppingCart: shoppingCartRequest,
                orderDetails: orderDetailsRequest,
            };
            storage.setItem(storageKey, JSON.stringify(organizationUnitData));
        }
        // Check if we need to perform a new login for the current user
        // It is possible we already have a user token for the target OU in the preserved data
        //
        let newApplicationToken = organizationUnitData[newOrganizationUnitID]?.applicationToken;
        let newUserToken = organizationUnitData[newOrganizationUnitID]?.authenticationToken;
        if (currentUserToken && !newUserToken) {
            try {
                const newLoginResponse = await evaEndpoint.callService(Core.Login, {
                    AuthenticationToken: currentUserToken,
                    OrganizationUnitID: newOrganizationUnitID,
                });
                newUserToken = newLoginResponse?.User?.AuthenticationToken;
                // The login call should also have returned a new application token
                //
                const applicationTokens = getServiceSettings().applicationTokens ?? {};
                const origin = new URL(evaEndpointUrl)?.origin;
                newApplicationToken = applicationTokens[origin];
            }
            catch (error) {
                logger.error(`Failed to login to new organization unit`, error)();
            }
        }
        else if (newApplicationToken) {
            // Re-apply the old application token for the service settings
            //
            const applicationTokens = getServiceSettings().applicationTokens ?? {};
            const origin = new URL(evaEndpointUrl)?.origin;
            applicationTokens[origin] = newApplicationToken;
            setServiceSetting('applicationTokens', applicationTokens);
        }
        // Populate the new state values
        //
        setApplicationToken(newApplicationToken);
        setCurrentUserToken(newUserToken ?? '');
        setRequestedOrganizationUnitId(newOrganizationUnitID);
        setOrderDetailsRequest(organizationUnitData[newOrganizationUnitID]?.orderDetails);
        setShoppingCartRequest(organizationUnitData[newOrganizationUnitID]?.shoppingCart);
    }, [
        evaEndpointUrl,
        currentUserToken,
        currentOrganizationUnitID,
        shoppingCartRequest,
        orderDetailsRequest,
    ]);
    return switchOrganizationUnit;
};
export default useSwitchOrganizationUnit;
//# sourceMappingURL=use-switch-organization-unit.js.map